import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  createPlatformGstRecord,
  getPlatformGstBootstrap,
  type PlatformGstBootstrap,
} from '../../api/dynamicGstConfigApi.js';
import './gst-config.css';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

const today = () => new Date().toISOString().slice(0, 10);
const text = (form: FormData, name: string) => String(form.get(name) ?? '').trim();
const optional = (form: FormData, name: string) => text(form, name) || undefined;

export function M17PlatformGstConfigPage(): ReactNode {
  const { token } = useAuth();
  const [asOn, setAsOn] = useState(today);
  const [data, setData] = useState<PlatformGstBootstrap | null>(null);
  const [section, setSection] = useState('JURISDICTIONS');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await getPlatformGstBootstrap(asOn, token);
      setData(result);
      if (!result.sections.some((item) => item.code === section)) setSection(result.sections[0]?.code ?? '');
    } catch (reason) { setError((reason as Error).message); }
  }, [asOn, section, token]);

  useEffect(() => { void load(); }, [load]);

  async function submit(event: FormEvent<HTMLFormElement>, resource: string, makeBody: (form: FormData) => unknown) {
    event.preventDefault();
    // React clears event.currentTarget once the handler yields, so hold the form first.
    const formElement = event.currentTarget;
    setBusy(true); setError(null); setNotice(null);
    try {
      await createPlatformGstRecord(resource, makeBody(new FormData(formElement)), token);
      formElement.reset();
      setNotice('Configuration saved.');
      await load();
    } catch (reason) { setError((reason as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main className="gst-workspace" aria-label="platform-gst-configuration">
      <header className="gst-header">
        <div><p className="gst-eyebrow">Platform master data</p><h1>Platform GST Configuration</h1></div>
        <label>Effective on<DateInput type="date" value={asOn} onValueChange={(nextValue) => setAsOn(nextValue)} /></label>
      </header>
      {error ? <p className="gst-message gst-error" role="alert">{error}</p> : null}
      {notice ? <p className="gst-message" role="status">{notice}</p> : null}
      {!data ? <p>Loading...</p> : (
        <>
          <nav className="gst-tabs" aria-label="GST configuration sections">
            {data.sections.map((item) => (
              <button key={item.code} type="button" aria-current={section === item.code ? 'page' : undefined}
                onClick={() => setSection(item.code)}>{item.label}</button>
            ))}
          </nav>
          {section === 'JURISDICTIONS' ? <Jurisdictions data={data} busy={busy} submit={submit} /> : null}
          {section === 'CLASSIFICATIONS' ? <Classifications data={data} busy={busy} submit={submit} /> : null}
          {section === 'TAX_COMPONENTS' ? <TaxComponents data={data} busy={busy} submit={submit} /> : null}
          {section === 'RATE_SCHEDULES' ? <RateSchedules data={data} busy={busy} submit={submit} /> : null}
          {section === 'OPTIONS' ? <Options data={data} busy={busy} submit={submit} /> : null}
        </>
      )}
    </main>
  );
}

type Submit = (event: FormEvent<HTMLFormElement>, resource: string, makeBody: (form: FormData) => unknown) => Promise<void>;
interface SectionProps { readonly data: PlatformGstBootstrap; readonly busy: boolean; readonly submit: Submit }
const Dates = () => <><label>Effective from<DateInput name="effectiveFromDate" type="date" required /></label><label>Effective to<DateInput name="effectiveToDate" type="date" /></label></>;

function Jurisdictions({ data, busy, submit }: SectionProps) {
  const { day } = useBusinessDay();
  return <section className="gst-section"><h2>Jurisdictions</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'jurisdictions', (form) => ({
      countryCode: text(form, 'countryCode').toUpperCase(), jurisdictionCode: text(form, 'jurisdictionCode').toUpperCase(),
      jurisdictionName: text(form, 'jurisdictionName'), jurisdictionType: text(form, 'jurisdictionType').toUpperCase(),
      gstStateCode: text(form, 'gstStateCode'), effectiveFromDate: text(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
    }))}>
      <label>Country code<input name="countryCode" maxLength={2} required /></label><label>Jurisdiction code<input name="jurisdictionCode" required /></label>
      <label>Name<input name="jurisdictionName" required /></label><label>Type<input name="jurisdictionType" required /></label>
      <label>GST state code<input name="gstStateCode" inputMode="numeric" maxLength={2} required /></label><Dates />
      <button disabled={busy || !data.capabilities.create}>Add jurisdiction</button>
    </form>
    <Table headers={['GST code', 'Jurisdiction', 'Type', 'Effective from', 'Effective to']} rows={data.jurisdictions.map((row) => [row.gstStateCode, row.jurisdictionName, row.jurisdictionType, day(row.effectiveFromDate), day(row.effectiveToDate)])} />
  </section>;
}

function Classifications({ data, busy, submit }: SectionProps) {
  const { day } = useBusinessDay();
  return <section className="gst-section"><h2>HSN / SAC classifications</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'classifications', (form) => ({
      classificationType: text(form, 'classificationType').toUpperCase(), classificationCode: text(form, 'classificationCode'),
      description: text(form, 'description'), chapterCode: optional(form, 'chapterCode'), unitCode: optional(form, 'unitCode'),
      effectiveFromDate: text(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
    }))}>
      <label>Type<input name="classificationType" required /></label><label>Code<input name="classificationCode" inputMode="numeric" required /></label>
      <label className="gst-span-2">Description<input name="description" required /></label><label>Chapter<input name="chapterCode" /></label>
      <label>Unit code<input name="unitCode" /></label><Dates /><button disabled={busy || !data.capabilities.create}>Add classification</button>
    </form>
    <Table headers={['Type', 'Code', 'Description', 'Unit', 'Effective from']} rows={data.classifications.map((row) => [row.classificationType, row.classificationCode, row.description, row.unitCode ?? '-', day(row.effectiveFromDate)])} />
    <StagedClassifications data={data} busy={busy} submit={submit} />
  </section>;
}

/** Codes staged from a source document and cross-checked against the official
 *  GST directory. Promoting one copies the verified code and description into
 *  the catalogue so nothing is retyped. The stated rate is shown only as a
 *  reading of the source: the rate itself is entered under Rate schedules, as
 *  components, because one staged row reads "12% or 18% (Depending on finish)"
 *  and no software should decide which of those a business meant. */
function StagedClassifications({ data, busy, submit }: SectionProps) {
  const pending = data.classificationImports.filter((row) => !row.alreadyPublished);
  if (data.classificationImports.length === 0) return null;
  return <div className="gst-subsection" data-testid="staged-classifications">
    <h3>Staged from source document</h3>
    {pending.length === 0 ? <p>Every staged code is already in the catalogue.</p> : (
      <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'classifications/promote', (form) => ({
        importId: text(form, 'importId'),
        verificationReference: text(form, 'verificationReference'),
        effectiveFromDate: text(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
      }))}>
        <label className="gst-span-2">Staged code
          <select name="importId" required data-testid="staged-import-select">
            <option value="">Select</option>
            {pending.map((row) => (
              <option key={row.id} value={row.id}>
                {row.classificationType} {row.classificationCode} - {row.description} (source states {row.statedRateText})
              </option>
            ))}
          </select>
        </label>
        <label className="gst-span-2">Verification reference<input name="verificationReference" required /></label>
        <Dates />
        <button disabled={busy || !data.capabilities.create}>Promote to catalogue</button>
      </form>
    )}
    <Table
      headers={['Type', 'Code', 'Description', 'Rate stated in source', 'Directory match', 'In catalogue']}
      rows={data.classificationImports.map((row) => [
        row.classificationType, row.classificationCode, row.description,
        row.statedRateText, row.directoryMatchStatus, row.alreadyPublished ? 'Yes' : 'No',
      ])} />
  </div>;
}

function TaxComponents({ data, busy, submit }: SectionProps) {
  const { day } = useBusinessDay();
  return <section className="gst-section"><h2>Tax components</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'tax-components', (form) => ({
      componentCode: text(form, 'componentCode').toUpperCase(), componentName: text(form, 'componentName'),
      supplyScopeCode: text(form, 'supplyScopeCode').toUpperCase(), calculationOrder: Number(text(form, 'calculationOrder')),
      effectiveFromDate: text(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
    }))}>
      <label>Component code<input name="componentCode" required /></label><label>Name<input name="componentName" required /></label>
      <label>Supply scope<select name="supplyScopeCode" required><option value="">Select</option>{data.options.filter((item) => item.optionGroupCode === 'SUPPLY_SCOPE').map((item) => <option key={item.gstOptionId} value={item.optionCode}>{item.optionLabel}</option>)}</select></label>
      <label>Calculation order<input name="calculationOrder" type="number" min="1" required /></label>
      <Dates /><button disabled={busy || !data.capabilities.create}>Add component</button>
    </form>
    <Table headers={['Order', 'Code', 'Name', 'Supply scope', 'Effective from']} rows={data.taxComponents.map((row) => [row.calculationOrder, row.componentCode, row.componentName, row.supplyScopeCode, day(row.effectiveFromDate)])} />
  </section>;
}

function RateSchedules({ data, busy, submit }: SectionProps) {
  const { day } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  return <section className="gst-section"><h2>Rate schedules</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'rate-schedules', (form) => ({
      classificationId: text(form, 'classificationId'), taxabilityType: text(form, 'taxabilityType').toUpperCase(),
      reverseCharge: form.get('reverseCharge') === 'on', notificationReference: optional(form, 'notificationReference'),
      effectiveFromDate: text(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
      components: data.taxComponents.filter((component) => form.get(`use-${component.taxComponentId}`) === 'on').map((component) => ({
        taxComponentId: component.taxComponentId, ratePct: Number(text(form, `rate-${component.taxComponentId}`)),
      })),
    }))}>
      <label>Classification<select name="classificationId" required><option value="">Select</option>{data.classifications.map((row) => <option key={row.classificationId} value={row.classificationId}>{row.classificationType} {row.classificationCode} - {row.description}</option>)}</select></label>
      <label>Taxability<select name="taxabilityType" required><option value="">Select</option>{data.options.filter((item) => item.optionGroupCode === 'TAXABILITY_TYPE').map((item) => <option key={item.gstOptionId} value={item.optionCode}>{item.optionLabel}</option>)}</select></label>
      <label>Notification reference<input name="notificationReference" /></label><label className="gst-check"><input name="reverseCharge" type="checkbox" /> Reverse charge</label>
      <fieldset className="gst-span-all"><legend>Components</legend>{data.taxComponents.map((component) => <div className="gst-rate-row" key={component.taxComponentId}><label className="gst-check"><input name={`use-${component.taxComponentId}`} type="checkbox" /> {component.componentName}</label><label>Rate %<input name={`rate-${component.taxComponentId}`} type="number" min="0" max="100" step="0.0001" /></label></div>)}</fieldset>
      <Dates /><button disabled={busy || !data.capabilities.create || data.taxComponents.length === 0}>Add rate schedule</button>
    </form>
    <Table headers={['Classification', 'Taxability', 'Components', 'Reverse charge', 'Effective from']} rows={data.rateSchedules.map((row) => {
      const classification = data.classifications.find((item) => item.classificationId === row.classificationId);
      const rates = data.rateComponents.filter((item) => item.rateScheduleId === row.rateScheduleId).map((item) => `${data.taxComponents.find((component) => component.taxComponentId === item.taxComponentId)?.componentCode ?? '?'} ${decimal(item.ratePct)}%`).join(', ');
      return [classification?.classificationCode ?? '—', row.taxabilityType, rates, row.reverseCharge ? 'Yes' : 'No', day(row.effectiveFromDate)];
    })} />
  </section>;
}

function Options({ data, busy, submit }: SectionProps) {
  const { day } = useBusinessDay();
  return <section className="gst-section"><h2>Configuration options</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'options', (form) => ({
      optionGroupCode: text(form, 'optionGroupCode').toUpperCase(), optionCode: text(form, 'optionCode').toUpperCase(),
      optionLabel: text(form, 'optionLabel'), displayOrder: Number(text(form, 'displayOrder')),
      effectiveFromDate: text(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
    }))}>
      <label>Option group<input name="optionGroupCode" required /></label><label>Option code<input name="optionCode" required /></label>
      <label>Display label<input name="optionLabel" required /></label><label>Display order<input name="displayOrder" type="number" min="1" required /></label>
      <Dates /><button disabled={busy || !data.capabilities.create}>Add option</button>
    </form>
    <Table headers={['Group', 'Code', 'Label', 'Order', 'Effective from']} rows={data.options.map((row) => [row.optionGroupCode, row.optionCode, row.optionLabel, row.displayOrder, day(row.effectiveFromDate)])} />
  </section>;
}

function Table({ headers, rows }: { readonly headers: readonly string[]; readonly rows: readonly (readonly ReactNode[])[] }) {
  return <div className="gst-table-wrap"><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>
    {rows.length === 0 ? <tr><td colSpan={headers.length}>No effective records.</td></tr> : rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}
  </tbody></table></div>;
}